const MANEUVER_META_KEYS = new Set([
	"formType",
	"maneuver",
	"maneuverCode",
	"assessmentId",
	"traineeAssessmentId",
	"candidateId",
	"selectedOBs",
	"safetyOutcome",
	"repeat",
]);

export type ObCountSummaryRow = {
	obType: string;
	pf: number;
	pm: number;
	total: number;
};

export type ObCountSummary = {
	rows: ObCountSummaryRow[];
	totals: { pf: number; pm: number; total: number };
};

export function emptyObCountSummary(obTypes: string[]): ObCountSummary {
	const rows = obTypes.map((obType) => ({ obType, pf: 0, pm: 0, total: 0 }));
	return { rows, totals: { pf: 0, pm: 0, total: 0 } };
}

/** Sum role-scoped OB currentCount per competency across all maneuvers. */
export function buildOverallObCountSummary(
	maneuvers: Array<Record<string, unknown>>,
	obTypes: string[],
): ObCountSummary {
	if (!obTypes.length) {
		return { rows: [], totals: { pf: 0, pm: 0, total: 0 } };
	}

	const counts = new Map<string, { pf: number; pm: number }>();
	for (const obType of obTypes) {
		counts.set(obType, { pf: 0, pm: 0 });
	}

	for (const maneuver of maneuvers) {
		for (const catKey of Object.keys(maneuver ?? {})) {
			if (MANEUVER_META_KEYS.has(catKey)) continue;

			const group = (maneuver[catKey] ?? {}) as Record<
				string,
				{ pf?: { currentCount?: number }; pm?: { currentCount?: number } }
			>;

			if (!counts.has(catKey)) {
				counts.set(catKey, { pf: 0, pm: 0 });
			}

			const bucket = counts.get(catKey)!;
			for (const obKey of Object.keys(group)) {
				const cell = group[obKey];
				bucket.pf += cell?.pf?.currentCount ?? 0;
				bucket.pm += cell?.pm?.currentCount ?? 0;
			}
		}
	}

	const orderedTypes = [
		...obTypes,
		...[...counts.keys()].filter((type) => !obTypes.includes(type)).sort(),
	];

	const rows: ObCountSummaryRow[] = [];
	let totalPf = 0;
	let totalPm = 0;

	for (const obType of orderedTypes) {
		const { pf, pm } = counts.get(obType) ?? { pf: 0, pm: 0 };
		const total = pf + pm;
		rows.push({ obType, pf, pm, total });
		totalPf += pf;
		totalPm += pm;
	}

	return {
		rows,
		totals: { pf: totalPf, pm: totalPm, total: totalPf + totalPm },
	};
}
